import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { getSectionItems, layoutHelpers } from 'ontraccr-form-to-pdf';
import { hasSavedSectionLayout } from '../pdfHelpers';

import PDFDesignerV3 from './v3/PDFDesignerV3';
import OnTraccrButton from '../../buttons/OnTraccrButton';

export default function PDFRepeatableSectionLayoutModal({
  open,
  onCancel,
  onSave,
  linkedSectionId,
  relevantSections,
  fileMap,
  initialSectionLayoutDrawOptions,
  formRef,
  name,
  typeId,
  collected,
  isExternalForm,
  modalKey,
}) {
  const repeatableLayoutSnapshotRef = useRef(null);
  const handleRepeatableLayoutChange = useCallback((snapshot) => {
    repeatableLayoutSnapshotRef.current = snapshot;
  }, []);

  useEffect(() => {
    if (!open) return;
    repeatableLayoutSnapshotRef.current = null;
  }, [open]);

  const [seedPages, setSeedPages] = useState();

  const sectionsForLayout = useMemo(() => {
    if (!linkedSectionId) return [];
    return relevantSections.filter((s) => s.id === linkedSectionId);
  }, [relevantSections, linkedSectionId]);

  const hasSaved = useMemo(
    () => hasSavedSectionLayout(initialSectionLayoutDrawOptions),
    [initialSectionLayoutDrawOptions],
  );

  useEffect(() => {
    if (!open || hasSaved || !linkedSectionId) {
      setSeedPages([[]]);
      return;
    }

    (async () => {
      try {
        const section = relevantSections.find((s) => s.id === linkedSectionId);
        if (!section) {
          setSeedPages([[]]);
          return;
        }
        const raw = await getSectionItems({
          sections: [section],
          offset: 0,
          settings: { showHiddenFields: true },
          convertRepeatableSections: false,
          linkRepeatableSectionTitle: true,
        });
        const parsed = raw.map((page) => (
          page.map((item) => layoutHelpers.calculateGridProperties({ ...item, deletable: true }))
        ));
        setSeedPages(parsed);
      } catch {
        setSeedPages([[]]);
      }
    })();
  }, [open, hasSaved, linkedSectionId, relevantSections]);

  const drawOptionsForDesigner = useMemo(() => {
    if (hasSaved) return initialSectionLayoutDrawOptions;
    if (seedPages) return seedPages;
    return [[]];
  }, [hasSaved, initialSectionLayoutDrawOptions, seedPages]);

  const handleSave = useCallback(() => {
    const snap = repeatableLayoutSnapshotRef.current;
    if (!snap?.pages) return;
    onSave({
      sectionLayoutDrawOptions: snap.pages,
    });
  }, [onSave]);

  const designerKey = useMemo(() => (
    `${modalKey}-${hasSaved ? 'saved' : 'seed'}-${drawOptionsForDesigner?.length ?? 0}-${drawOptionsForDesigner?.[0]?.length ?? 0}`
  ), [modalKey, hasSaved, drawOptionsForDesigner]);

  return (
    <Modal
      title="Configure repeatable section layout"
      className="cost-tracking-analysis-detail-modal"
      visible={open}
      onCancel={onCancel}
      width="95vw"
      style={{ top: 16, paddingBottom: 0 }}
      zIndex={1100}
      bodyStyle={{
        height: '80vh',
        overflowY: 'auto',
        padding: '0 0 16px',
      }}
      footer={[
        <OnTraccrButton onClick={onCancel} title="Cancel" type="cancel" />,
        <OnTraccrButton onClick={handleSave} title="Save layout" type="primary" />,
      ]}
      destroyOnClose
      maskClosable={false}
    >
      <p
        style={{
          marginBottom: 12,
          color: '#666',
          fontSize: 13,
          paddingTop: 12,
        }}
      >
        Arrange fields and static content for this repeatable section only.
        PDF will expand this design into your repeatable section block.
      </p>
      <PDFDesignerV3
        key={designerKey}
        repeatableSectionLayoutMode
        onRepeatableLayoutChange={handleRepeatableLayoutChange}
        lockedRepeatableLayoutSectionId={linkedSectionId}
        formRef={formRef}
        name={name}
        typeId={typeId}
        collected={collected}
        sections={sectionsForLayout}
        isDisplay={false}
        currentStep={3}
        drawOptions={drawOptionsForDesigner}
        fileMap={fileMap}
        useStandardTemplate={false}
        isExternalForm={isExternalForm}
        prevUseStandardTemplate={false}
        pdfName=""
      />
    </Modal>
  );
}

PDFRepeatableSectionLayoutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  linkedSectionId: PropTypes.string,
  relevantSections: PropTypes.arrayOf(PropTypes.shape({})),
  fileMap: PropTypes.shape({}),
  initialSectionLayoutDrawOptions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  formRef: PropTypes.shape({ current: PropTypes.shape({}) }),
  name: PropTypes.string,
  typeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collected: PropTypes.shape({}),
  isExternalForm: PropTypes.bool,
  modalKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PDFRepeatableSectionLayoutModal.defaultProps = {
  linkedSectionId: null,
  relevantSections: [],
  fileMap: {},
  initialSectionLayoutDrawOptions: null,
  formRef: null,
  name: '',
  typeId: undefined,
  collected: {},
  isExternalForm: false,
  modalKey: '',
};
